import {useEffect} from "react"

/**
 * A custom hook that persists and restores scroll position for a scrollable element.
 *
 * Tracks scroll position as a ratio (0-1) rather than absolute pixels,
 * making it resilient to vertical container resizes (e.g. secondaryWorkspace panel).
 *
 * @param {HTMLElement|null} scrollEl - The scrollable DOM element, typically
 *   obtained from a ref applied to the container element. Can accept values
 *   from a ref passed via context API, like the TabPanelScrollContext in
 *   the <TabPanel /> component.
 *
 * @param {Object} options - passed as an Object to support optional values
 *
 * @param {function} [options.savedPosition] - The current saved position, if it currently exists.
 *   often pulled from a selector defined in the calling component.
 *
 * @param {function} [options.setPosition] - Callback that receives and stores
 *   the scroll ratio (0-1). Often a dispatched Redux Action.
 *
 * @param {boolean} options.isActive - Whether the panel is currently visible.
 *   The scroll listener only attaches when true, and restoration only
 *   occurs when true.
 *
 * @param {Array} [options.restoreDeps = []] - Additional dependencies that
 *   trigger scroll restoration when they change (e.g. sub-tab index,
 *   active view key). These are spread into the restore effect's
 *   dependency array.
 *
 * @param {boolean} [options.restoreConditional = true] - Additional guard
 *   for scroll restoration. When false, the restore effect exits early
 *   even if the panel is active. Useful for panels that should only
 *   restore under specific conditions.
 *
 * @example
 * // Using Redux values
 * const dispatch = useDispatch()
 * const currentScrollPosition = useSelector(selectPanelPosition)
 *
 * useScrollPersistence(scrollEl, {
 *   savedPosition: currentScrollPosition,
 *   setPosition: (ratio) => dispatch(setNotificationScrollPosition(ratio)),
 *   isActive: displayingNotificationsPanel,
 *   restoreDeps: [currentPanelViewIndex],
 *   restoreConditional: articles.length <= 0,
 * });
 *
 * @example
 * // Callback mode — no Redux
 * const [scrollRatio, setScrollRatio] = useState(0)
 *
 * useScrollPersistence(scrollEl, {
 *   savedPositionNoSelector: scrollRatio,
 *   setPosition: setScrollRatio,
 *   isActive: true,
 * });
 */
export function useScrollPersistence(scrollEl, {
	savedPosition,
	setPosition,
	isActive,
	restoreDeps = [],
	restoreConditional = true
}) {
	// Attach scroll listener UseEffect
	useEffect(() => {
		// if no element or element is not active, return
		if (!scrollEl || !isActive) return

		let ticking = false
		const handleScroll = () => {
			if (!ticking) {
				ticking = true
				requestAnimationFrame(() => {
					// measure max scroll position
					const maxScroll = scrollEl.scrollHeight - scrollEl.clientHeight
					const ratio = maxScroll > 0
						? scrollEl.scrollTop / maxScroll
						: 0
					setPosition(ratio)
					ticking = false
				})
			}
		}
		scrollEl.addEventListener("scroll", handleScroll)

		//cleanup
		return () => scrollEl.removeEventListener("scroll", handleScroll)
	}, [scrollEl, isActive, setPosition])

	// Restore scroll position useEffect
	useEffect(() => {
		// note restoreConditional: if set, adds an extra requirement for restoring scrollPos.
		if (!scrollEl || !isActive || !restoreConditional) return
		const maxScroll = scrollEl.scrollHeight - scrollEl.clientHeight
		scrollEl.scrollTop = savedPosition * maxScroll
	}, [scrollEl, isActive, ...restoreDeps])
}